import React from 'react';
import styles from './PostDetail.module.css';
import PostBody from './PostBody';
import { attachmentDisplayName } from '../../utils/attachments';

async function triggerDownload(url, preferredName) {
  const filename = preferredName || 'download';
  try {
    // cache: 'no-store' is load-bearing. The <img> above renders this same URL, and a
    // plain <img> caches an opaque (non-CORS) response. Reusing that cached entry makes
    // this fetch fail CORS even though S3 allows the origin — which sent us down the
    // catch branch and opened the file in a tab instead of downloading it.
    const res = await fetch(url, { mode: 'cors', cache: 'no-store' });
    if (!res.ok) throw new Error();
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  } catch {
    // Cross-origin fallback: open in new tab
    window.open(url, '_blank');
  }
}

const REACTIONS = [
  { key: 'like',        emoji: '👍', label: 'Like' },
  { key: 'acknowledge', emoji: '🧡', label: 'Acknowledge' },
  { key: 'useful',      emoji: '💡', label: 'Useful' },
  { key: 'excited',     emoji: '🚀', label: 'Excited' },
];

function formatDate(isoString) {
  return new Date(isoString).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function AuthorAvatar({ author }) {
  const initial = author.name.charAt(0).toUpperCase();
  const isSuspire = author.type === 'suspire';

  if (isSuspire) {
    return (
      <div className={`${styles.avatar} ${styles.avatarSuspire}`}>
        <img src="/images/Suspire_Logo.png" alt="Suspire"
          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
      </div>
    );
  }
  if (author.logo_url) {
    return (
      <div className={`${styles.avatar} ${styles.avatarBrandLogo}`}>
        <img src={author.logo_url} alt={author.name}
          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
          onError={e => { e.target.style.display = 'none'; e.target.parentNode.textContent = initial; }} />
      </div>
    );
  }
  return (
    <div className={`${styles.avatar} ${styles.avatarBrand}`}>
      {initial}
    </div>
  );
}

export default function PostDetail({ post, postTypes, onClose, onReact, isAdmin }) {
  const typeInfo = postTypes[post.post_type] || { label: post.post_type, color: '#64748b' };
  const totalReactions = Object.values(post.reactions).reduce((a, b) => a + b, 0);

  const hasImage = post.attachment_url && post.attachment_type === 'IMAGE';
  const hasPdf   = post.attachment_url && post.attachment_type === 'PDF';

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className={styles.overlay} onClick={handleBackdrop}>
      <div className={styles.panel}>

        {/* ── Top bar ── */}
        <div className={styles.topBar}>
          <button className={styles.backBtn} onClick={onClose}>
            ← Back to Circle
          </button>
          <span
            className={styles.typeBadge}
            style={{ background: typeInfo.color + '18', color: typeInfo.color, borderColor: typeInfo.color + '40' }}
          >
            {typeInfo.label}
          </span>
        </div>

        {/* ── Scrollable content ── */}
        <div className={styles.content}>

          {/* Author row */}
          <div className={styles.authorRow}>
            <AuthorAvatar author={post.author} />
            <div>
              <span className={styles.authorName}>
                {post.author.name}
                {post.author.type === 'suspire' && (
                  <span className={styles.officialBadge}>Official</span>
                )}
              </span>
              <span className={styles.timestamp}>{formatDate(post.created_at)}</span>
            </div>
          </div>

          {/* Title */}
          <h1 className={styles.title}>{post.title}</h1>

          {/* Body — full, no truncation */}
          <PostBody post={post} className={styles.body} htmlClassName={styles.richBody} />

          {/* Media image — actions hidden for gated posts */}
          {hasImage && (
            <div className={styles.mediaContainer}>
              <img
                src={post.attachment_url}
                alt="Post media"
                className={styles.mediaImage}
              />
              {!post.visibility_gated && (
                <div className={styles.imageActions}>
                  <a
                    href={post.attachment_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.imageActionBtn}
                  >
                    View Full Size
                  </a>
                  <button
                    className={`${styles.imageActionBtn} ${styles.imageActionDownload}`}
                    onClick={() => triggerDownload(post.attachment_url, attachmentDisplayName(post, 'image'))}
                  >
                    Download
                  </button>
                </div>
              )}
            </div>
          )}

          {/* PDF attachment — download hidden for gated posts */}
          {hasPdf && (
            <div className={styles.attachment}>
              <span className={styles.attachmentIcon}>📄</span>
              {post.visibility_gated ? (
                <span className={styles.attachmentName}>
                  {attachmentDisplayName(post, 'Document.pdf')}
                </span>
              ) : (
                <a
                  className={styles.attachmentNameButton}
                  href={post.attachment_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Click to preview"
                >
                  {attachmentDisplayName(post, 'Document.pdf')}
                </a>
              )}
              {!post.visibility_gated && (
                <>
                  {/* An explicit action next to the filename, so preview is not
                      something you have to guess is behind the name. */}
                  <a
                    className={styles.attachmentPreview}
                    href={post.attachment_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open Preview
                  </a>
                  <button
                    className={styles.attachmentDownload}
                    onClick={() => triggerDownload(post.attachment_url, attachmentDisplayName(post, 'Document.pdf'))}
                  >
                    Download
                  </button>
                </>
              )}
            </div>
          )}

          {/* CTA link */}
          {post.cta_url && (
            <a
              href={post.cta_url}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.ctaLink}
            >
              {post.cta_label || 'Learn More'} →
            </a>
          )}

          {/* Audience note */}
          {post.visibility_gated && post.gated_plan_name && (
            <div className={styles.audienceTag}>
              Requires {post.gated_plan_name} plan or above
            </div>
          )}

          {/* Reactions — hidden for admin */}
          {!isAdmin && (
            <div className={styles.reactionsSection}>
              <p className={styles.reactionsTitle}>Reactions</p>
              <div className={styles.reactionsRow}>
                {REACTIONS.map(r => (
                  <button
                    key={r.key}
                    className={`${styles.reactionBtn} ${post.user_reaction === r.key ? styles.reactionActive : ''}`}
                    onClick={() => onReact(post.id, r.key)}
                    title={r.label}
                  >
                    <span className={styles.reactionEmoji}>{r.emoji}</span>
                    <span className={styles.reactionLabel}>{r.label}</span>
                    <span className={styles.reactionCount}>{post.reactions[r.key] || 0}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
